import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { Editor, EditorState, ContentState, convertFromRaw } from "draft-js";
import {
  fileExtensionIsImage,
  convertNumberToSplitStation,
} from "../../../_utils/generalUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ADR_REVIEW } from "../../../_constants/actionConstants";
import { adrSections } from "../../../_constants/docSections";
import { AdrCorrectionCommentsV3 } from "./AdrCorrectionCommentsV3";
import "draft-js/dist/Draft.css";
import adrActions from "../../../_actions/adrActions";
import { useCallback } from "react";

export function AdrReviewNarrativesV3(props) {
  const { tabId, wpSortOrder } = props;
  const adr = useSelector((state) => state.adr.openAdrs[tabId] || null);
  const appSettings = useSelector((state) => state.appSettings);
  const user = useSelector((state) => state.authentication.user || null);

  const dispatch = useDispatch();

  const memoizedLocWpRows = useMemo(
    () =>
      adr
        ? (wpSortOrder || [])
            .filter((wpSo) => !adr.workPerformedClient[wpSo.wpTabId].isIdle)
            .map((wpSo) =>
              adr.workPerformedClient[wpSo.wpTabId].locationWorkPerformed.map(
                (locWp) => ({ ...locWp, wpTabId: wpSo.wpTabId })
              )
            )
            .flat()
        : [],
    [wpSortOrder, adr.workPerformedClient]
  );

  const isNarrativeCc = useCallback((cc, fieldInfo) => {
    // All cc's in this section must have fieldInfo, at least one that
    // references wpId/locId.
    if (!cc.fieldInfo) return false;
    if (
      cc.fieldInfo.workPerformedId !== fieldInfo.workPerformedId ||
      cc.fieldInfo.locationId !== fieldInfo.locationId
    )
      return false;
    if (
      cc.fieldInfo.workPerformedId === fieldInfo.workPerformedId &&
      cc.fieldInfo.locationId === fieldInfo.locationId
    ) {
      // If the minimum required fieldInfo is provided, check further if
      // sublocWpId is provided, and if so, whether it matches what's provided.
      if (
        "sublocationId" in cc.fieldInfo &&
        "sublocationWorkPerformedId" in cc.fieldInfo &&
        cc.fieldInfo.sublocationId !== 0 &&
        cc.fieldInfo.sublocationWorkPerformedId !== fieldInfo.sublocationWorkPerformedId
      )
        return false;
    }
    return true;
  }, []);

  const isMetadataMatching = useCallback(
    (a, b) =>
      a.workPerformedId === b.workPerformedId &&
      a.locationId === b.locationId &&
      a.sublocationId === b.sublocationId &&
      a.sublocationWorkPerformedId === b.sublocationWorkPerformedId,
    []
  );

  /**
   * Determines whether the provided field has a correction comment
   * referencing it. Used for redlining the commented fields, and to prevent
   * adding duplicate correction comments to one that has one already.
   * Note: difference between hasCorrectionComment and isNarrativeCc (also
   * a func in this component) is that hasCorrectionComment takes a field
   * and looks up whether cc exists (for highlighting), whereas isNarrativeCc
   * takes a comment and determines whether it belongs in the specified locWp
   * row (for filtering).
   * @param {import("../../../_typedefs/adrV3TypeDefs").FieldInfo} fieldInfoToLookup
   * @returns {null|string}
   */
  const hasCorrectionComment = useCallback(
    (fieldInfoToLookup) => {
      const ccBase = (adr.correctionComments || []).filter(
        (c) =>
          c.section === adrSections.ADR_SECTION_NARRATIVE &&
          c.fieldInfo &&
          "workPerformedId" in c.fieldInfo &&
          "locationId" in c.fieldInfo &&
          "sublocationId" in c.fieldInfo &&
          "sublocationWorkPerformedId" in c.fieldInfo &&
          isMetadataMatching(c.fieldInfo, fieldInfoToLookup)
      );
      const cc =
        fieldInfoToLookup.name === "attachment"
          ? ccBase.find((c) => c.fieldInfo.lwpaId === fieldInfoToLookup.lwpaId)
          : ccBase.find((c) => fieldInfoToLookup.name === c.fieldInfo.name);
      return cc ? true : false;
    },
    [adr.correctionComments, isMetadataMatching]
  );

  const addCorrectionComment = (fieldInfo) => {
    if (user) {
      dispatch({
        type: ADR_REVIEW.CORRECTION_COMMENTS.ADD_BLANK,
        tabId,
        byUserId: user.userId,
        section: adrSections.ADR_SECTION_NARRATIVE,
        fieldInfo,
      });
    }
  };

  return (
    <div>
      <h4>Location and Description of Operation</h4>
      {memoizedLocWpRows.map((locWp, index) => {
        const wpc = adr ? adr.workPerformedClient[locWp.wpTabId] : null;
        if (!wpc) return null;

        // metadataBase has all common field references for its correctionComment
        // for locationWorkPerformed. (See 'hasCorrectionComment' for more on how it's
        // used.) All children that inherit from metadataBase contain UNIQUE
        // reference to the actual field.
        const metadataBase = {
          workPerformedId: locWp.workPerformedId,
          locationId: locWp.locationId,
          sublocationId: locWp.isSublocation ? locWp.sublocationId : 0,
          sublocationWorkPerformedId: locWp.isSublocation
            ? locWp.sublocationWorkPerformedId
            : 0,
          reviewerInitials: props.reviewerInitials,
        };

        const filteredCcs = (adr.correctionComments || []).filter(
          (cc) =>
            cc.section === adrSections.ADR_SECTION_NARRATIVE &&
            isNarrativeCc(cc, metadataBase)
        );

        return (
          <AdrReviewNarrativeRowV3
            key={`reviewLocWpKey_${index}`}
            wpc={wpc}
            locWp={locWp}
            lwpIndex={index}
            metadataBase={metadataBase}
            isInReview={adr.isInReview}
            hasCorrectionComment={hasCorrectionComment}
            addCorrectionComment={addCorrectionComment}
            appSettings={appSettings}
            {...props}
          >
            <AdrCorrectionCommentsV3
              section={adrSections.ADR_SECTION_NARRATIVE}
              correctionComments={filteredCcs}
              genericMetadata={{
                workPerformedId: locWp.workPerformedId,
                locationId: locWp.locationId,
              }}
              {...props}
            />
          </AdrReviewNarrativeRowV3>
        );
      })}
    </div>
  );
}

export function AdrReviewNarrativeRowV3(props) {
  const {
    projectId,
    hasCorrectionComment,
    addCorrectionComment,
    metadataBase,
    locWp,
    lwpIndex,
    wpc,
    isInReview,
    userIsReviewer,
  } = props;

  const dispatch = useDispatch();

  const projLocationList = useSelector(
    (state) => state.locations.projLocationLists[projectId] || null
  );

  const handleClick_field = (fieldInfo) => (e) => {
    e.preventDefault();
    if (!hasCorrectionComment(fieldInfo) && isInReview && userIsReviewer)
      addCorrectionComment(fieldInfo);
  };

  const handleClick_openImage = (filePath) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(adrActions.openFile(filePath));
  };

  const renderAttachmentThumbnail = (lwpa) => {
    const ext = lwpa.originalFileName
      ? lwpa.originalFileName.split(".").pop().toLowerCase()
      : "";
    if (fileExtensionIsImage(ext)) {
      return (
        <div
          onClick={handleClick_openImage(lwpa.filePath)}
          className="pointer-cursor"
        >
          <img
            src={lwpa.thumbnailBase64}
            alt="(Cannot display thumbnail)"
            height="90"
            width="auto"
          />
        </div>
      );
    } else {
      let icon = "file";
      switch (ext) {
        case "doc":
        case "docx":
          icon = "file-word";
          break;
        case "xls":
        case "xlsx":
          icon = "file-excel";
          break;
        case "pdf":
          icon = "file-pdf";
          break;
        case "zip":
          icon = "file-archive";
          break;
        default:
          break;
      }
      return (
        <div onClick={handleClick_openImage(lwpa.filePath)} className="pointer-cursor">
          <FontAwesomeIcon icon={["far", icon]} className="fa-fw fa-3x" />
        </div>
      );
    }
  };

  const getInspectionType = (inspTypeId) =>
    ["None", "Benchmark", "Intermittent", "Continuous"][+inspTypeId] || "(N/A)";

  const getLocationName = (locWp) => {
    if (locWp.locationId < -1) {
      // If location is Line-Sta:
      if (locWp.locationId === -2) {
        // Get the line name & sta. ranges
        const line = (projLocationList || []).find(
          (l) => l.locationId === locWp.sublocationId
        );
        const staFromOrAt = locWp.stationFromOrAt
          ? convertNumberToSplitStation(locWp.stationFromOrAt)
          : null;
        const staTo = locWp.stationTo
          ? convertNumberToSplitStation(locWp.stationTo)
          : null;
        const sta = staFromOrAt
          ? staTo
            ? `, from ${staFromOrAt.fullSta}+${staFromOrAt.partialSta} to ${staTo.fullSta}+${staTo.partialSta}`
            : `, ${staFromOrAt.fullSta}+${staFromOrAt.partialSta}`
          : "";
        return line ? `"${line.name}" Line${sta}` : "(N/A)";
      } else return "(N/A)"; // Add others as needed
    } else {
      if (projLocationList)
        return locWp.locationId
          ? (projLocationList || []).find((l) => l.locationId === locWp.locationId)?.name ?? "(N/A)"
          : "(N/A)";
    }
  };

  const locName = useMemo(() => getLocationName(locWp), [locWp]);
  const inspType = useMemo(() => getInspectionType(locWp.inspectionType), [locWp]);
  const wpShortCaption = useMemo(() => (wpc ? wpc.wpShortCaption : ""), [wpc]);
  const wpFullCaption = useMemo(() => (wpc ? wpc.wpFullCaption : ""), [wpc]);
  const wpUnitOfMeasure = useMemo(() => (wpc ? wpc.unitOfMeasure : ""), [wpc]);

  const locNameInfo = {
    ...metadataBase,
    name: "locationName",
    value: locName,
    label: "Location",
    subLabel: `${locName} (for ${wpShortCaption})`,
  };
  const inspTypeInfo = {
    ...metadataBase,
    name: "inspectionType",
    value: inspType,
    label: "Insp. Type",
    subLabel: `${inspType} (${wpShortCaption} at ${locName})`,
  };
  const qtyInfo = {
    ...metadataBase,
    name: "quantity",
    value: locWp.quantity || "0",
    label: "Quantity",
    subLabel: `(${wpShortCaption} at ${locName})`,
  };
  const isControllingOpInfo = {
    ...metadataBase,
    name: "isControllingOperation",
    value: locWp.isControllingOperation,
    label: "Controlling Operation",
    subLabel: `(${wpShortCaption} at ${locName})`,
  };
  const isDisputedInfo = {
    ...metadataBase,
    name: "isDisputed",
    value: locWp.isDisputed,
    label: "Disputed Work",
    subLabel: `(${wpShortCaption} at ${locName})`,
  };
  const workDescInfo = {
    ...metadataBase,
    name: "workDescription",
    value: locWp.workDescription,
    label: "Work Desc",
    subLabel: `(for ${wpShortCaption} at ${locName})`,
  };

  const editorState = useMemo(
    () =>
      locWp.workDescriptionDraftState
        ? EditorState.createWithContent(
            convertFromRaw(JSON.parse(locWp.workDescriptionDraftState))
          )
        : locWp.workDescription
        ? EditorState.createWithContent(
            ContentState.createFromText(locWp.workDescription)
          )
        : EditorState.createWithContent(ContentState.createFromText("(None)")),
    [locWp.workDescriptionDraftState, locWp.workDescription]
  );

  const editorIsEmpty =
    !editorState.getCurrentContent().hasText() ||
    ["", "(None)"].includes(editorState.getCurrentContent().getPlainText());

  return (
    <div key={`wpDataFragKey_${wpc.wpTabId}`} className="row my-2 pb-2 border-bottom">
      <div className="col-lg-9">
        <div className="card">
          <div className="card-body p-2">
            <div className="row mx-1 py-1 border-top border-bottom bg-lightgray">
              <div className="col px-1 pr-0">
                <h6 className="text-info">
                  <b>{wpFullCaption}</b>
                </h6>
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(locNameInfo) ? "has-comment" : ""
                  }`}
                  onClick={handleClick_field(locNameInfo)}
                >
                  <b>Location:</b> {locName}
                </div>
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(inspTypeInfo) ? "has-comment" : ""
                  }`}
                  onClick={handleClick_field(inspTypeInfo)}
                >
                  <b>Inspection Type:</b> {inspType}
                </div>
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(qtyInfo) ? "has-comment" : ""
                  }`}
                  onClick={handleClick_field(qtyInfo)}
                >
                  <b>Quantity:</b> {locWp.quantity || "0"} {wpUnitOfMeasure}
                </div>
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(isControllingOpInfo) ? "has-comment" : ""
                  }`}
                  onClick={handleClick_field(isControllingOpInfo)}
                >
                  <b>Controlling Operation:</b>{" "}
                  <FontAwesomeIcon
                    icon={
                      locWp.isControllingOperation ? "check-square" : ["far", "square"]
                    }
                    className="fa-fw"
                  />
                </div>
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(isDisputedInfo) ? "has-comment" : ""
                  }`}
                  onClick={handleClick_field(isDisputedInfo)}
                >
                  <b>Disputed Work:</b>{" "}
                  <FontAwesomeIcon
                    icon={locWp.isDisputed ? "check-square" : ["far", "square"]}
                    className="fa-fw"
                  />
                </div>
              </div>
            </div>
            <div className="row mx-1">
              <div className="col pl-1">
                <b>Work Description:</b>
              </div>
            </div>
            <div className="row mx-1">
              <div className="col px-1">
                <div
                  className={`${userIsReviewer && isInReview ? "reviewable-item" : ""} ${
                    hasCorrectionComment(workDescInfo) ? "has-comment" : ""
                  } ${editorIsEmpty ? "editor-empty" : ""}`}
                  onClick={handleClick_field(workDescInfo)}
                >
                  <div
                    style={{ overflowX: "auto", borderRadius: "3px" }}
                    className="review border"
                  >
                    <Editor
                      name="workDescription"
                      id={`locWorkDescription_${wpc.wpTabId}_${lwpIndex}`}
                      editorState={editorState}
                      onChange={() => {}}
                      onBlur={() => {}}
                      readOnly={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            {locWp.attachments && locWp.attachments.length > 0 && (
              <React.Fragment>
                <div className="row mx-1">
                  <div className="col pl-1">
                    <b>Attachments:</b>
                  </div>
                </div>
                {locWp.attachments
                  .filter((lwpa) => !lwpa.isDeleted)
                  .map((lwpa, lwpaRowIndex) => {
                    const lwpaId = locWp.isSublocation
                      ? lwpa.sublocationAttachmentId
                      : lwpa.locationWorkPerformedAttachmentId;
                    const lwpAttachmentInfo = {
                      ...metadataBase,
                      lwpaId,
                      name: "attachment",
                      value: lwpaId,
                      label: "Attachment",
                      subLabel: `(${lwpa.originalFileName} at ${locName})`,
                    };
                    return (
                      <div
                        key={`lwpaDataKey_${lwpaRowIndex}`}
                        className={`row m-1 px-1 ${
                          userIsReviewer && isInReview ? "reviewable-item" : ""
                        } ${
                          hasCorrectionComment(lwpAttachmentInfo) ? "has-comment" : ""
                        }`}
                        onClick={handleClick_field(lwpAttachmentInfo)}
                      >
                        <div className="col-md-3">{renderAttachmentThumbnail(lwpa)}</div>
                        <div className="col-md-8">
                          <small>
                            <b>Filename:</b> {lwpa.originalFileName}
                            <br />
                            <b>Created On:</b> {moment(lwpa.createdOn).format("LLLL")}
                            <br />
                            <b>Description:</b> {lwpa.description}
                          </small>
                        </div>
                      </div>
                    );
                  })}
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-12 comment-column border-left">
        {props.children}
      </div>
    </div>
  );
}
